import {
  actionCreator,
  reducedStream,
  reducer,
  ofType,
  extractPayload,
  Action,
} from '@ardoq/rxbeach';
import { withLatestFrom, tap, Subject, filter } from 'rxjs';
import { ANIMATION_END_DURATION, TOAST_DURATION } from './animationUtils';
import { Toast, Toasts, ToastType, ToastPosition } from './types';
import { logError } from '@ardoq/logging';
import { ReactElement } from 'react';
import { random } from 'lodash';

// This module is designed to keep the ToastManager entirely self-contained. It
// manages its own action and error streams internally, minimizing its public
// interface. Consumers should primarily interact with it via the showToast
// function and by rendering the ToastManager component.

const action$ = new Subject<Action<unknown>>();
const errorSubject = new Subject<any>();
errorSubject.subscribe(logError);

const defaultToast = {
  error: false,
  id: 0,
  index: 0,
  info: false,
  message: '',
  removeToast: () => null,
  shouldMoveUp: false,
  success: false,
  type: ToastType.INFO,
  position: ToastPosition.TOP_RIGHT,
};

export const showToast = (
  message: string | ReactElement,
  type: ToastType = ToastType.INFO,
  position: ToastPosition = ToastPosition.TOP_RIGHT,
  options?: {
    timeout?: boolean;
    actionButton?: { label: string; onClick: () => void };
    maxWidth?: 'fit-content';
  }
): VoidFunction => {
  const toast = {
    ...defaultToast,
    id: random(1, Number.MAX_SAFE_INTEGER),
    message,
    type,
    position,
    ...options,
  };

  if (position === ToastPosition.BOTTOM_RIGHT) {
    action$.next(toastBottomReceived(toast));
  } else {
    action$.next(toastTopReceived(toast));
  }

  const removeToastFunc = () => {
    if (position === ToastPosition.BOTTOM_RIGHT) {
      action$.next(toastBottomExpired(toast.id));
    } else {
      action$.next(toastTopExpired(toast.id));
    }
  };

  return removeToastFunc;
};

const toastTopReceived = actionCreator<Toast>(
  '[ToastManager] TOAST_TOP_RECEIVED'
);
const toastBottomReceived = actionCreator<Toast>(
  '[ToastManager] TOAST_BOTTOM_RECEIVED'
);
const toastTopExpired = actionCreator<number>(
  '[ToastManager] TOAST_TOP_TIMED_OUT'
);
const toastBottomExpired = actionCreator<number>(
  '[ToastManager] TOAST_BOTTOM_TIMED_OUT'
);
const manyToastsTopVisible = actionCreator(
  '[ToastManager] MANY_TOASTS_TOP_VISIBLE'
);
const manyToastsBottomVisible = actionCreator(
  '[ToastManager] MANY_TOASTS_BOTTOM_VISIBLE'
);

const toViewProps = (
  { toasts, idIndexMap }: Toasts,
  expireAction: (id: number) => Action<number>
): Toasts => {
  const allIds = new Set(toasts.map(({ id }) => id));
  const newIdIndexMap = Object.fromEntries(
    Object.entries(idIndexMap).filter(([id]) => allIds.has(Number(id)))
  );
  const updatedToasts = toasts.map(
    (
      { id, type, message, position, timeout, actionButton, maxWidth },
      index
    ) => {
      const shouldMoveUp = idIndexMap[id] > index;
      newIdIndexMap[id] = index;
      return {
        id,
        index,
        message,
        removeToast: () => action$.next(expireAction(id)),
        shouldMoveUp,
        type,
        position,
        timeout,
        actionButton,
        maxWidth,
      };
    }
  );
  return {
    toasts: updatedToasts,
    idIndexMap: newIdIndexMap,
  };
};

const toastTopReceivedReducer = (
  { toasts, idIndexMap }: Toasts,
  toast: Toast
) =>
  toViewProps(
    {
      toasts: [toast, ...toasts],
      idIndexMap,
    },
    toastTopExpired
  );

const toastBottomReceivedReducer = (
  { toasts, idIndexMap }: Toasts,
  toast: Toast
) =>
  toViewProps(
    {
      toasts: [toast, ...toasts],
      idIndexMap,
    },
    toastBottomExpired
  );

const toastTopExpiredReducer = ({ toasts, idIndexMap }: Toasts, id: number) =>
  toViewProps(
    {
      toasts: toasts.filter(toast => toast.id !== id),
      idIndexMap,
    },
    toastTopExpired
  );

const toastBottomExpiredReducer = (
  { toasts, idIndexMap }: Toasts,
  id: number
) =>
  toViewProps(
    {
      toasts: toasts.filter(toast => toast.id !== id),
      idIndexMap,
    },
    toastBottomExpired
  );

const manyToastsTopVisibleReducer = ({ toasts, idIndexMap }: Toasts) =>
  toViewProps(
    {
      toasts: toasts.slice(0, -1),
      idIndexMap,
    },
    toastTopExpired
  );

const manyToastsBottomVisibleReducer = ({ toasts, idIndexMap }: Toasts) =>
  toViewProps(
    {
      toasts: toasts.slice(0, -1),
      idIndexMap,
    },
    toastBottomExpired
  );

const expireTopToasts = ([{ id }, { toasts }]: [Toast, Toasts]) => {
  setTimeout(
    () => action$.next(toastTopExpired(id)),
    TOAST_DURATION - ANIMATION_END_DURATION / 2
  );
  if (toasts.length > 2) {
    setTimeout(
      () => action$.next(manyToastsTopVisible()),
      ANIMATION_END_DURATION - 100
    );
  }
};

const expireBottomToasts = ([{ id }, { toasts }]: [Toast, Toasts]) => {
  setTimeout(
    () => action$.next(toastBottomExpired(id)),
    TOAST_DURATION - ANIMATION_END_DURATION / 2
  );
  if (toasts.length > 2) {
    setTimeout(
      () => action$.next(manyToastsBottomVisible()),
      ANIMATION_END_DURATION - 100
    );
  }
};

export const toastTop$ = reducedStream<Toasts>(
  'toastTop$',
  { toasts: [], idIndexMap: {} },
  [
    reducer(toastTopReceived, toastTopReceivedReducer),
    reducer(toastTopExpired, toastTopExpiredReducer),
    reducer(manyToastsTopVisible, manyToastsTopVisibleReducer),
  ],
  {
    action$,
    errorSubject,
  }
);

export const toastBottom$ = reducedStream<Toasts>(
  'toastBottom$',
  { toasts: [], idIndexMap: {} },
  [
    reducer(toastBottomReceived, toastBottomReceivedReducer),
    reducer(toastBottomExpired, toastBottomExpiredReducer),
    reducer(manyToastsBottomVisible, manyToastsBottomVisibleReducer),
  ],
  {
    action$,
    errorSubject,
  }
);

const toastTopRoutine = action$.pipe(
  ofType(toastTopReceived),
  extractPayload(),
  filter(toast => toast.timeout !== false),
  withLatestFrom(toastTop$),
  tap(expireTopToasts)
);

const toastBottomRoutine = action$.pipe(
  ofType(toastBottomReceived),
  extractPayload(),
  filter(toast => toast.timeout !== false),
  withLatestFrom(toastBottom$),
  tap(expireBottomToasts)
);

const subscribe = () => {
  toastTopRoutine.subscribe({
    error(error: any) {
      errorSubject.next(error);
      subscribe();
    },
  });
  toastBottomRoutine.subscribe({
    error(error: any) {
      errorSubject.next(error);
      subscribe();
    },
  });
};

subscribe();
